import styled from 'styled-components';

import { Stack } from '@components/stack';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export type HeaderProps = {
  actions?: JSX.Element;
  breadcrumbs?: JSX.Element;
  tabs?: JSX.Element;
  filters?: JSX.Element;
  backAction?: JSX.Element;
  title?: JSX.Element;
  selectionHeader?: JSX.Element;
  showSelectionHeader?: boolean;
  hideTabs?: boolean;
};

export const Header = ({
  actions,
  breadcrumbs,
  tabs,
  filters,
  backAction,
  title,
  selectionHeader,
  showSelectionHeader = false,
  hideTabs = false,
}: HeaderProps) => {
  const hasTabs = Boolean(tabs) && !hideTabs;
  const hasFilters = Boolean(filters);
  return (
    <Container
      $hasTabs={hasTabs}
      $hasFilters={hasFilters}
      $isSelection={showSelectionHeader}
      id="header"
    >
      {showSelectionHeader ? (
        selectionHeader
      ) : (
        <>
          <MainHeaderContent>
            <InnerContainer hasTabs={hasTabs}>
              <Stack space={2} orientation="horizontal" align="center" id="header-left">
                {backAction}
                {title ? title : breadcrumbs ? <Breadcrumbs>{breadcrumbs}</Breadcrumbs> : null}
              </Stack>
              {tabs && !hideTabs && <TabsContainer id="header-tabs">{tabs}</TabsContainer>}
            </InnerContainer>
            <HeaderActions id="header-actions">{actions}</HeaderActions>
          </MainHeaderContent>
          {hasFilters && (
            <>
              <FiltersSeparator />
              <FiltersContainer>{filters}</FiltersContainer>
            </>
          )}
        </>
      )}
    </Container>
  );
};

const HeaderHeightWithTabs = '7.5rem';
export const HeaderHeightDefault = '5.5rem';

const Container = styled.div<{ $hasTabs: boolean; $hasFilters: boolean; $isSelection: boolean }>(
  ({ $hasTabs, $hasFilters, $isSelection }) => ({
    display: 'flex',
    flexDirection: $hasFilters ? 'column' : 'row',
    padding: pxToRem(24),
    paddingBottom: $hasTabs || $hasFilters ? '0' : pxToRem(24),
    transition: 'min-height 0.2s ease-out',
    justifyContent: $hasFilters ? 'flex-start' : 'space-between',
    minHeight: $hasTabs ? HeaderHeightWithTabs : HeaderHeightDefault,
    backgroundColor: $isSelection
      ? theme.color.backgroundSecondary
      : theme.color.backgroundLightDark,
    borderBottom: `${pxToRem(1)} solid ${
      $isSelection ? theme.color.uiTrans10 : theme.color.border10
    }`,
    zIndex: 10,
  })
);

const MainHeaderContent = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
});

const InnerContainer = styled.div<{ hasTabs: boolean }>(({ hasTabs }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: hasTabs ? 'space-between' : 'center',
}));

const Breadcrumbs = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  height: '2.5rem',
});

const TabsContainer = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
});

export const HeaderActions = styled.div({
  flexGrow: 1,
  display: 'flex',
  justifyContent: 'flex-end',
  position: 'relative',
  alignItems: 'flex-start',
});

const FiltersSeparator = styled.div({
  width: `calc(100% + ${pxToRem(48)})`,
  height: pxToRem(1),
  backgroundColor: theme.color.border10,
  marginLeft: pxToRem(-24),
  marginRight: pxToRem(-24),
  marginTop: pxToRem(16),
  marginBottom: pxToRem(12),
});

const FiltersContainer = styled.div({
  display: 'flex',
  paddingBottom: pxToRem(16),
});
